import type { ComputedRef, Ref } from 'vue'

/**
 * Error state for quantity validation
 */
export type QuantityValidationErrorState = 'below-min' | 'beyond-max' | null

/**
 * Return type for useQuantityValidation composable
 */
export interface UseQuantityValidationOptions {
  onBelowMin?: () => void
  onBeyondMax?: () => void
  onResetErrorState?: () => void
}

/**
 * Return type for useQuantityValidation composable
 */
export interface UseQuantityValidationReturn {
  /**
   * Current error state ref. Can be 'below-min', 'beyond-max', or null.
   * Automatically updates when quantity violates min/max constraints.
   */
  errorState: Ref<QuantityValidationErrorState>

  /**
   * Computed boolean indicating if quantity is below the minimum.
   * True when errorState is 'below-min', false otherwise.
   */
  triedToGoBelowMin: ComputedRef<boolean>

  /**
   * Computed boolean indicating if quantity exceeds the maximum.
   * True when errorState is 'beyond-max', false otherwise.
   */
  triedToGoBeyondMax: ComputedRef<boolean>

  /**
   * Resets the error state to null.
   * Useful for manually clearing validation errors.
   */
  resetErrorState: () => void

  /**
   * Manually sets the error state.
   * Useful for custom validation logic or user-triggered errors.
   *
   * @param state - The error state to set ('below-min', 'beyond-max', or null)
   */
  setErrorState: (state: QuantityValidationErrorState) => void
}

/**
 * Composable for quantity validation error states.
 *
 * Automatically validates a quantity against min/max constraints and provides
 * error state management. The validation runs reactively whenever quantity,
 * minQty, or maxQty changes.
 *
 * @param quantity - The current quantity to validate (ref or computed ref)
 * @param minQty - The minimum allowed quantity (ref or computed ref)
 * @param maxQty - The maximum allowed quantity (ref or computed ref)
 * @returns Object containing error state and helper functions
 *
 * @example
 * ```ts
 * const quantity = ref(5)
 * const minQty = computed(() => 1)
 * const maxQty = computed(() => 10)
 *
 * const {
 *   triedToGoBelowMin,
 *   triedToGoBeyondMax,
 *   errorState,
 *   setErrorState
 * } = useQuantityValidation(quantity, minQty, maxQty)
 *
 * // errorState automatically updates when quantity changes
 * quantity.value = 15 // errorState.value becomes 'beyond-max'
 * quantity.value = 0  // errorState.value becomes 'below-min'
 * quantity.value = 5  // errorState.value becomes null
 *
 * // Use in template
 * // <span v-if="triedToGoBeyondMax">Too many tickets!</span>
 * ```
 */
export const useQuantityValidation = (
  quantity: Ref<number> | ComputedRef<number>,
  minQty: Ref<number> | ComputedRef<number>,
  maxQty: Ref<number> | ComputedRef<number>,
  options: UseQuantityValidationOptions = {}
): UseQuantityValidationReturn => {
  const {
    onBelowMin = () => {},
    onBeyondMax = () => {},
    onResetErrorState = () => {},
  } = options
  const errorState = ref<QuantityValidationErrorState>(null)

  const triedToGoBelowMin = computed(() => errorState.value === 'below-min')
  const triedToGoBeyondMax = computed(() => errorState.value === 'beyond-max')

  // Watch for constraint violations
  watchEffect(() => {
    if (quantity.value > maxQty.value) {
      errorState.value = 'beyond-max'
      onBeyondMax()
    } else if (quantity.value < minQty.value) {
      errorState.value = 'below-min'
      onBelowMin()
    } else {
      errorState.value = null
      onResetErrorState()
    }
  })

  const resetErrorState = (): void => {
    errorState.value = null
  }

  const setErrorState = (state: QuantityValidationErrorState): void => {
    errorState.value = state
  }

  return {
    errorState,
    triedToGoBelowMin,
    triedToGoBeyondMax,
    resetErrorState,
    setErrorState,
  } satisfies UseQuantityValidationReturn
}
